import React, { useEffect, useState, useMemo, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiDownload, FiFileText, FiChevronDown, FiX, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import Modal from '../../../../components/Modal';
import AddNewLocationSurveyModal from '../components/AddNewLocationSurveyModal';
import NewLocationSurveyDetailsModal from '../components/NewLocationSurveyDetailsModal';
import {
    fetchNewLocationSurveysPage,
    fetchNewLocationSurveysMatchingAll,
    fetchNewLocationSurveyPsoNames,
    computeNewLocationSurveyStatusLabel,
    deleteNewLocationSurvey,
    updateInchargeReadStatus,
    type NewLocationSurvey,
    type NewLocationSurveyPageCursor,
    type LocationSurveyStatusFilter,
    type LocationSurveyHazardFilter,
} from '../service/newLocationSurveyService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../../components/Table';
import { designationIsIncharge } from '../../../../utils/designationRole';

export default function NewLocationSurveys() {
    const [filtered, setFiltered] = useState<NewLocationSurvey[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [pageCursors, setPageCursors] = useState<Record<number, NewLocationSurveyPageCursor | null>>({});
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedSurvey, setSelectedSurvey] = useState<NewLocationSurvey | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsAutoEdit, setDetailsAutoEdit] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [rowContextMenu, setRowContextMenu] = useState<{
        survey: NewLocationSurvey;
        clientX: number;
        clientY: number;
    } | null>(null);

    const [requestDateFrom, setRequestDateFrom] = useState<string | null>(null);
    const [requestDateTo, setRequestDateTo] = useState<string | null>(null);
    const [draftRequestDateFrom, setDraftRequestDateFrom] = useState<string | null>(null);
    const [draftRequestDateTo, setDraftRequestDateTo] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<LocationSurveyStatusFilter>('all');
    const [draftStatusFilter, setDraftStatusFilter] = useState<LocationSurveyStatusFilter>('all');
    const [hazardousFilter, setHazardousFilter] = useState<LocationSurveyHazardFilter>('all');
    const [draftHazardousFilter, setDraftHazardousFilter] = useState<LocationSurveyHazardFilter>('all');

    const [surveyDateFrom, setSurveyDateFrom] = useState<string | null>(null);
    const [surveyDateTo, setSurveyDateTo] = useState<string | null>(null);
    const [draftSurveyDateFrom, setDraftSurveyDateFrom] = useState<string | null>(null);
    const [draftSurveyDateTo, setDraftSurveyDateTo] = useState<string | null>(null);
    const [assignedPsoFilter, setAssignedPsoFilter] = useState<string>('all');
    const [draftAssignedPsoFilter, setDraftAssignedPsoFilter] = useState<string>('all');
    const [psoNameOptions, setPsoNameOptions] = useState<string[]>([]);

    const [scanPool, setScanPool] = useState<NewLocationSurvey[] | null>(null);
    const [hitScanLimit, setHitScanLimit] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const [updatingReadStatus, setUpdatingReadStatus] = useState<string | null>(null);

    const [exportProgress, setExportProgress] = useState<{
        active: boolean;
        format: 'excel' | 'pdf' | null;
        percent: number;
        message: string;
        totalRows: number;
        processedRows: number;
        done: boolean;
    }>({
        active: false,
        format: null,
        percent: 0,
        message: '',
        totalRows: 0,
        processedRows: 0,
        done: false,
    });
    const cancelExportRef = useRef(false);

    const handleSetReadStatus = async (survey: NewLocationSurvey, newReadStatus: boolean) => {
        if (!survey.bankId || !survey.id) return;
        setUpdatingReadStatus(survey.id);
        try {
            await updateInchargeReadStatus(survey.bankId, survey.id, newReadStatus);
            setFiltered(prev => prev.map(item => item.id === survey.id ? { ...item, inchargeRead: newReadStatus } : item));
            if (scanPool !== null) {
                setScanPool(prev => prev ? prev.map(item => item.id === survey.id ? { ...item, inchargeRead: newReadStatus } : item) : null);
            }
            showToast(`Survey marked as ${newReadStatus ? 'Read' : 'Unread'}`, 'success');
        } catch (error: any) {
            console.error('Error updating read status:', error);
            showToast(`Failed to update read status: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setUpdatingReadStatus(null);
        }
    };

    const hasActiveLocationFilters =
        !!(requestDateFrom && requestDateTo) ||
        !!(surveyDateFrom && surveyDateTo) ||
        statusFilter !== 'all' ||
        hazardousFilter !== 'all' ||
        (assignedPsoFilter && assignedPsoFilter !== 'all');

    const needsClientScan = useMemo(
        () =>
            appliedSearchTerm.trim() !== '' ||
            !!(requestDateFrom && requestDateTo) ||
            !!(surveyDateFrom && surveyDateTo) ||
            statusFilter !== 'all' ||
            hazardousFilter !== 'all' ||
            (assignedPsoFilter && assignedPsoFilter !== 'all'),
        [
            appliedSearchTerm,
            requestDateFrom,
            requestDateTo,
            surveyDateFrom,
            surveyDateTo,
            statusFilter,
            hazardousFilter,
            assignedPsoFilter,
        ]
    );

    const exportRows = useMemo(
        () => (scanPool !== null ? scanPool : filtered),
        [scanPool, filtered]
    );

    const paginatedData = useMemo(() => {
        if (scanPool !== null) {
            const start = (currentPage - 1) * perPage;
            return scanPool.slice(start, start + perPage);
        }
        return filtered;
    }, [scanPool, filtered, currentPage, perPage]);

    const totalPages = useMemo(() => {
        if (scanPool !== null) return Math.max(1, Math.ceil(scanPool.length / perPage));
        return hasMore ? currentPage + 1 : Math.max(1, currentPage);
    }, [scanPool, hasMore, currentPage, perPage]);

    const exportPeriodSlug = useMemo(() => {
        const parts: string[] = [];
        if (requestDateFrom && requestDateTo) parts.push(`req_${requestDateFrom}_${requestDateTo}`);
        if (surveyDateFrom && surveyDateTo) parts.push(`sur_${surveyDateFrom}_${surveyDateTo}`);
        if (assignedPsoFilter && assignedPsoFilter !== 'all') {
            parts.push(`pso_${assignedPsoFilter.replace(/\s+/g, '_').slice(0, 40)}`);
        }
        return parts.length ? parts.join('__') : 'all';
    }, [requestDateFrom, requestDateTo, surveyDateFrom, surveyDateTo, assignedPsoFilter]);

    const periodLabelForPdf = useMemo(() => {
        const bits: string[] = [];
        if (requestDateFrom && requestDateTo) bits.push(`Request ${requestDateFrom} – ${requestDateTo}`);
        if (surveyDateFrom && surveyDateTo) bits.push(`Survey ${surveyDateFrom} – ${surveyDateTo}`);
        if (assignedPsoFilter && assignedPsoFilter !== 'all') bits.push(`PSO: ${assignedPsoFilter}`);
        return bits.length ? bits.join('; ') : 'All records';
    }, [requestDateFrom, requestDateTo, surveyDateFrom, surveyDateTo, assignedPsoFilter]);

    const uid = auth.currentUser?.uid;
    const psoProfile = useAppSelector((s) => s.user.pso);
    const designation = psoProfile?.designation || '';
    const normalizedDesignation = useMemo(() => designation.replace(/_/g, ' ').toLowerCase(), [designation]);
    const userRole = typeof window !== 'undefined' ? sessionStorage.getItem('role') : null;
    const isBankUser = userRole === 'bank';
    const isIncharge = useMemo(() => isBankUser || designationIsIncharge(designation), [isBankUser, designation]);
    const isMIS = useMemo(() => normalizedDesignation.includes('mis'), [normalizedDesignation]);
    const isRegional = useMemo(() => normalizedDesignation.includes('regional'), [normalizedDesignation]);
    const isHead = useMemo(() => normalizedDesignation === 'head' || normalizedDesignation === 'head pso', [normalizedDesignation]);
    const isPsoOnly = useMemo(() => !isBankUser && !isMIS && !isRegional && !isHead, [isBankUser, isMIS, isRegional, isHead]);

    useEffect(() => {
        void loadPage(1, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearchTerm, requestDateFrom, requestDateTo, surveyDateFrom, surveyDateTo, statusFilter, hazardousFilter, assignedPsoFilter, uid, normalizedDesignation]);

    useEffect(() => {
        if (!showFilterModal) return;
        let cancelled = false;
        void (async () => {
            try {
                const names = await fetchNewLocationSurveyPsoNames({ maxDocumentsToScan: 2500 });
                if (!cancelled) setPsoNameOptions(names);
            } catch {
                if (!cancelled) setPsoNameOptions([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showFilterModal]);

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [exportMenuOpen]);

    useEffect(() => {
        if (!rowContextMenu) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setRowContextMenu(null);
        };
        const onDown = (e: MouseEvent) => {
            const menu = document.getElementById('location-surveys-row-context');
            if (menu?.contains(e.target as Node)) return;
            setRowContextMenu(null);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, [rowContextMenu]);

    const handleSurveyAdded = (message?: string, submittedSurvey?: NewLocationSurvey) => {
        showToast(message || 'Survey saved successfully', 'success');
        void loadPage(currentPage, true);
        if (submittedSurvey && !isRegional && !isHead && !isPsoOnly) {
            exportSingleSurveyToPDF(submittedSurvey);
        }
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    const applySearch = () => {
        setAppliedSearchTerm(searchInput.trim());
    };

    const openFilterModal = () => {
        setDraftRequestDateFrom(requestDateFrom);
        setDraftRequestDateTo(requestDateTo);
        setDraftSurveyDateFrom(surveyDateFrom);
        setDraftSurveyDateTo(surveyDateTo);
        setDraftAssignedPsoFilter(assignedPsoFilter);
        setDraftStatusFilter(statusFilter);
        setDraftHazardousFilter(hazardousFilter);
        setShowFilterModal(true);
    };

    const applyFilters = () => {
        if (draftRequestDateFrom && draftRequestDateTo) {
            setRequestDateFrom(draftRequestDateFrom);
            setRequestDateTo(draftRequestDateTo);
        } else {
            setRequestDateFrom(null);
            setRequestDateTo(null);
        }
        if (draftSurveyDateFrom && draftSurveyDateTo) {
            setSurveyDateFrom(draftSurveyDateFrom);
            setSurveyDateTo(draftSurveyDateTo);
        } else {
            setSurveyDateFrom(null);
            setSurveyDateTo(null);
        }
        setAssignedPsoFilter(draftAssignedPsoFilter || 'all');
        setStatusFilter(draftStatusFilter);
        setHazardousFilter(draftHazardousFilter);
        setShowFilterModal(false);
    };

    const clearFilters = () => {
        setRequestDateFrom(null);
        setRequestDateTo(null);
        setSurveyDateFrom(null);
        setSurveyDateTo(null);
        setAssignedPsoFilter('all');
        setStatusFilter('all');
        setHazardousFilter('all');
        setDraftRequestDateFrom(null);
        setDraftRequestDateTo(null);
        setDraftSurveyDateFrom(null);
        setDraftSurveyDateTo(null);
        setDraftAssignedPsoFilter('all');
        setDraftStatusFilter('all');
        setDraftHazardousFilter('all');
        setScanPool(null);
        setHitScanLimit(false);
    };

    const loadPage = async (page: number, reset = false) => {
        if (needsClientScan && scanPool !== null && !reset && page !== 1) {
            setCurrentPage(page);
            return;
        }

        setLoading(true);
        try {
            if (needsClientScan) {
                const res = await fetchNewLocationSurveysMatchingAll({
                    match: {
                        searchContains: appliedSearchTerm.trim() || undefined,
                        requestDateFromYmd: requestDateFrom,
                        requestDateToYmd: requestDateTo,
                        surveyDateFromYmd: surveyDateFrom,
                        surveyDateToYmd: surveyDateTo,
                        assignedPsoName: assignedPsoFilter !== 'all' ? assignedPsoFilter : null,
                        statusFilter,
                        hazardousFilter,
                    },
                    maxDocumentsToScan: 4000,
                    chunkSize: 400,
                });

                let list = res.surveys;
                if (isRegional && !isHead) {
                    list = list.filter((survey) => {
                        const status = (survey.status || '').toLowerCase();
                        return ['submitted', 'submitted to regional', 'forwarded', 'approved', 'rejected', 'cancelled', 'completed'].includes(status);
                    });
                }
                if (isPsoOnly) {
                    list = list.filter((survey) => survey.assignedTo === uid);
                }

                setScanPool(list);
                setHitScanLimit(res.hitScanLimit);
                setFiltered([]);
                setPageCursors({});
                setHasMore(false);
                setCurrentPage(1);

                if (res.hitScanLimit) {
                    showToast(
                        'Scan limit reached: only the most recent records were searched. Refine search or filters to narrow results.',
                        'info'
                    );
                }
                return;
            }

            setHitScanLimit(false);
            setScanPool(null);

            const prevCursor = page === 1 ? null : pageCursors[page - 1] || null;
            const res = await fetchNewLocationSurveysPage({
                pageSize: perPage,
                cursor: reset ? null : prevCursor,
            });

            let list = res.surveys;
            if (isRegional && !isHead) {
                list = list.filter((survey) => {
                    const status = (survey.status || '').toLowerCase();
                    return ['submitted', 'submitted to regional', 'forwarded', 'approved', 'rejected', 'cancelled', 'completed'].includes(status);
                });
            }
            if (isPsoOnly) {
                list = list.filter((survey) => survey.assignedTo === uid);
            }

            setFiltered(list);
            setCurrentPage(page);
            setHasMore(res.hasMore);
            setPageCursors((prev) => ({
                ...(reset ? {} : prev),
                [page]: res.cursor,
            }));
        } catch (e) {
            console.error('Error loading location surveys page:', e);
            setFiltered([]);
            setScanPool(null);
        } finally {
            setLoading(false);
        }
    };

    const openDetailsModal = (survey: NewLocationSurvey, autoEdit = false) => {
        setSelectedSurvey(survey);
        setDetailsAutoEdit(autoEdit);
        setShowDetailsModal(true);
    };

    const handleDeleteSurvey = async (survey: NewLocationSurvey) => {
        if (!survey.bankId) {
            showToast('Cannot delete: survey bank details missing', 'error');
            return;
        }
        if (window.confirm(`Are you sure you want to delete survey ${survey.surveyId}?`)) {
            try {
                await deleteNewLocationSurvey(survey.bankId, survey.id);
                showToast('Survey deleted successfully', 'success');
                void loadPage(currentPage, true);
            } catch (e: any) {
                showToast(e.message || 'Failed to delete survey', 'error');
            }
        }
    };

    const exportToExcel = async () => {
        cancelExportRef.current = false;
        const total = exportRows.length;
        setExportProgress({ active: true, format: 'excel', percent: 10, message: 'Preparing survey records...', totalRows: total, processedRows: 0, done: false });

        await new Promise(r => setTimeout(r, 300));
        if (cancelExportRef.current) {
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        setExportProgress(p => ({ ...p, percent: 40, message: 'Processing fields and questions...' }));

        const worksheetData = exportRows.map((survey, index) => {
            const surveyQuestions = (survey.questions || {}) as Record<string, string>;
            const hazardFromQuestions = surveyQuestions.fuelStationAvailable === 'Yes' || surveyQuestions.otherAssessmentHazardous === 'Yes';
            const hazardFromDetails = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
            const hazardFlag = survey.isHazardous ?? (hazardFromQuestions || hazardFromDetails);
            return {
                'Survey ID': survey.surveyId || 'N/A',
                'Address': survey.locationAddress || 'N/A',
                'Survey Request Date': survey.surveyRequestDate || 'N/A',
                'Survey Date': survey.surveyDate || 'N/A',
                'Survey Submitted On': survey.surveySubmittedOn || 'N/A',
                'Space Consists Of': survey.spaceConsistsOf || 'N/A',
                'Hazardous': hazardFlag ? 'Yes' : 'No',
                'Hazardous Details': survey.hazardousArea || 'N/A',
                'Directions': survey.directions ? Object.entries(survey.directions).map(([key, value]) => `${key}: ${value}`).join(', ') : 'N/A',
                'Questions': survey.questions ? Object.entries(survey.questions).map(([key, value]) => `${key}: ${value}`).join('; ') : 'N/A',
                'Remarks': survey.remarks || 'N/A',
            };
        });

        if (cancelExportRef.current) {
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        setExportProgress(p => ({ ...p, percent: 70, message: 'Generating Excel sheet...' }));
        await new Promise(r => setTimeout(r, 350));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Location Surveys');

        const colWidths = [
            { wch: 20 }, // Survey ID
            { wch: 40 }, // Address
            { wch: 18 }, // Survey Request Date
            { wch: 15 }, // Survey Date
            { wch: 18 }, // Survey Submitted On
            { wch: 30 }, // Space Consists Of
            { wch: 12 }, // Hazardous
            { wch: 30 }, // Hazardous Details
            { wch: 40 }, // Directions
            { wch: 60 }, // Questions
            { wch: 40 }, // Remarks
        ];
        worksheet['!cols'] = colWidths;

        const fileName = `Location_Survey_Reports_${exportPeriodSlug}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        setExportProgress(p => ({ ...p, percent: 100, message: 'Excel file exported successfully!', done: true }));
        showToast('Excel file exported successfully', 'success');
    };

    const exportSingleSurveyToPDF = (survey: NewLocationSurvey) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const primaryColor: [number, number, number] = [0, 131, 123];
        const accentColor: [number, number, number] = [0, 131, 123];

        // Header Banner
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(0, 0, 210, 26, 'F');

        // Title (Centered)
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('LOCATION SURVEY REPORT', 105, 16, { align: 'center' });

        const dateStr = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        // Metadata grid
        const metadataRows = [
            [
                { content: 'Survey No:', styles: { fontStyle: 'bold' as const } },
                survey.surveyId || 'N/A',
                { content: 'Status:', styles: { fontStyle: 'bold' as const } },
                computeNewLocationSurveyStatusLabel(survey)
            ],
            [
                { content: 'Request Date:', styles: { fontStyle: 'bold' as const } },
                survey.surveyRequestDate || 'N/A',
                { content: 'Survey Date:', styles: { fontStyle: 'bold' as const } },
                survey.surveyDate || 'N/A'
            ],
            [
                { content: 'Space Type:', styles: { fontStyle: 'bold' as const } },
                survey.spaceConsistsOf || 'N/A',
                { content: 'Assigned PSO:', styles: { fontStyle: 'bold' as const } },
                survey.assignedToName || 'N/A'
            ]
        ];

        autoTable(doc, {
            startY: 32,
            theme: 'plain',
            body: metadataRows,
            bodyStyles: { fontSize: 8.5, cellPadding: 1.5 },
            columnStyles: {
                0: { cellWidth: 28 },
                1: { cellWidth: 62 },
                2: { cellWidth: 28 },
                3: { cellWidth: 62 }
            }
        });

        // Address Box
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Branch Address']],
            body: [[survey.locationAddress || 'N/A']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 3 }
        });

        // Directions Box
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Front (North)', 'Back (South)', 'Left (West)', 'Right (East)']],
            body: [[
                survey.directions?.front || 'N/A',
                survey.directions?.back || 'N/A',
                survey.directions?.left || 'N/A',
                survey.directions?.right || 'N/A'
            ]],
            headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 3 }
        });

        // Questions Box
        const qQuestions = survey.questions || {};
        const questionRows = [
            ['Fuel Station Available Near Location?', qQuestions.fuelStationAvailable || 'No'],
            ['Space Available for Parking?', qQuestions.spaceForParking || 'No'],
            ['Space Available for Vantage Point?', qQuestions.spaceForVantagePoint || 'No'],
            ['Space Available for Emergency Exit?', qQuestions.spaceForEmergencyExit || 'No'],
            ['Electricity Issue?', qQuestions.electricityIssue || 'No'],
            ['PTCL Line Available?', qQuestions.ptclLineAvailable || 'No'],
            ['Any Other Assessment?', qQuestions.otherAssessment || 'No']
        ];

        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Assessment Question', 'Answer']],
            body: questionRows,
            headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 140 },
                1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' as const }
            },
            bodyStyles: { fontSize: 8.5, cellPadding: 2 }
        });

        const lastY = (doc as any).lastAutoTable.finalY;
        const hazardFromQuestions = qQuestions.fuelStationAvailable === 'Yes' || qQuestions.otherAssessmentHazardous === 'Yes';
        const hazardFromDetails = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
        const hazardFlag = survey.isHazardous ?? (hazardFromQuestions || hazardFromDetails);

        if (hazardFlag) {
            doc.setFillColor(254, 226, 226);
            doc.rect(15, lastY + 4, 180, 18, 'F');
            doc.setDrawColor(239, 68, 68);
            doc.rect(15, lastY + 4, 180, 18, 'S');
            doc.setTextColor(185, 28, 28);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9.5);
            doc.text('HAZARD ASSESSMENT WARNING: HAZARDOUS LOCATION DETECTED', 18, lastY + 9);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(127, 29, 29);
            doc.text(`Details: ${survey.hazardousArea || 'Fuel station nearby or other hazards present.'}`, 18, lastY + 15);
        } else {
            doc.setFillColor(240, 253, 244);
            doc.rect(15, lastY + 4, 180, 11, 'F');
            doc.setDrawColor(34, 197, 94);
            doc.rect(15, lastY + 4, 180, 11, 'S');
            doc.setTextColor(21, 128, 61);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9.5);
            doc.text('HAZARD ASSESSMENT: SECURE / NON-HAZARDOUS', 18, lastY + 11);
        }

        const remarksY = lastY + (hazardFlag ? 27 : 20);

        // Professional Remarks Table
        autoTable(doc, {
            startY: remarksY,
            theme: 'grid',
            head: [['Remarks']],
            body: [[survey.remarks || 'No remarks provided.']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 4 }
        });

        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${survey.assignedToName || 'System'}      |      Date: ${dateStr}`, 15, pageHeight - 9);

        const fileName = `Survey_Report_${survey.surveyId || 'N/A'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('Single Survey PDF exported successfully', 'success');
    };

    const exportToPDF = async () => {
        if (exportRows.length === 0) {
            showToast('No surveys to export', 'warning');
            return;
        }

        cancelExportRef.current = false;
        const total = exportRows.length;
        setExportProgress({ active: true, format: 'pdf', percent: 10, message: 'Compiling summary stats...', totalRows: total, processedRows: 0, done: false });

        await new Promise(r => setTimeout(r, 300));
        if (cancelExportRef.current) {
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        setExportProgress(p => ({ ...p, percent: 35, message: 'Building PDF layout...' }));

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const themeColor: [number, number, number] = [0, 131, 123];

        // Title Header
        doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
        doc.rect(0, 0, 210, 26, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('LOCATION SURVEYS SUMMARY REPORT', 105, 16, { align: 'center' });

        const dateStr = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        // Stats calculations
        const statusStats: Record<string, number> = {};
        const spaceStats: Record<string, number> = {};
        const hazardStats: Record<string, number> = { 'Hazardous': 0, 'Safe': 0 };
        const totalSurveys = exportRows.length;

        exportRows.forEach(survey => {
            // Status
            const status = computeNewLocationSurveyStatusLabel(survey);
            statusStats[status] = (statusStats[status] || 0) + 1;

            // Space Consists Of
            const space = survey.spaceConsistsOf || 'N/A';
            spaceStats[space] = (spaceStats[space] || 0) + 1;

            // Hazard
            const q = survey.questions || {};
            const hQ = q.fuelStationAvailable === 'Yes' || q.otherAssessmentHazardous === 'Yes';
            const hD = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
            const isH = survey.isHazardous ?? (hQ || hD);
            if (isH) {
                hazardStats['Hazardous'] += 1;
            } else {
                hazardStats['Safe'] += 1;
            }
        });

        const statusRows = Object.entries(statusStats).map(([k, v]) => [k, v]);
        statusRows.push(['Total', totalSurveys]);

        const hazardRows = Object.entries(hazardStats).map(([k, v]) => [k, v]);
        hazardRows.push(['Total', totalSurveys]);

        const spaceRows = Object.entries(spaceStats).map(([k, v]) => [k, v]);
        spaceRows.push(['Total', totalSurveys]);

        // Side-by-side stats tables
        // Table 1: Status (Width: 55)
        autoTable(doc, {
            startY: 32,
            margin: { left: 15 },
            tableWidth: 55,
            theme: 'grid',
            head: [['Status Scope', 'Count']],
            body: statusRows,
            headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
        });

        // Table 2: Hazard (Width: 55)
        autoTable(doc, {
            startY: 32,
            margin: { left: 78 },
            tableWidth: 55,
            theme: 'grid',
            head: [['Hazard Status', 'Count']],
            body: hazardRows,
            headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
        });

        // Table 3: Space Type (Width: 55)
        autoTable(doc, {
            startY: 32,
            margin: { left: 140 },
            tableWidth: 55,
            theme: 'grid',
            head: [['Space Type', 'Count']],
            body: spaceRows,
            headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 7.5, cellPadding: 1.5 },
        });

        const lastY = Math.max(
            (doc as any).lastAutoTable?.finalY || 95,
            95
        );

        const detailsStartY = lastY + 10;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(51, 65, 85);
        doc.text('SURVEY DETAILS LIST', 15, detailsStartY - 3);

        if (cancelExportRef.current) {
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        setExportProgress(p => ({ ...p, percent: 65, message: 'Processing table rows...' }));

        const tableBody = exportRows.map(survey => {
            const q = survey.questions || {};
            const hQ = q.fuelStationAvailable === 'Yes' || q.otherAssessmentHazardous === 'Yes';
            const hD = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
            const isH = survey.isHazardous ?? (hQ || hD);
            return [
                survey.surveyId || 'N/A',
                survey.locationAddress || 'N/A',
                survey.surveyDate || 'N/A',
                survey.spaceConsistsOf || 'N/A',
                isH ? 'Yes' : 'No',
                computeNewLocationSurveyStatusLabel(survey),
            ];
        });

        if (cancelExportRef.current) {
            setExportProgress(p => ({ ...p, active: false }));
            return;
        }

        setExportProgress(p => ({ ...p, percent: 85, message: 'Rendering PDF pages...' }));
        await new Promise(r => setTimeout(r, 300));

        autoTable(doc, {
            startY: detailsStartY,
            theme: 'grid',
            head: [['Survey ID', 'Location Address', 'Survey Date', 'Space Type', 'Hazardous', 'Status']],
            body: tableBody,
            headStyles: { fillColor: themeColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 7.5, cellPadding: 2 },
            columnStyles: {
                0: { cellWidth: 22 },
                1: { cellWidth: 68 },
                2: { cellWidth: 22 },
                3: { cellWidth: 33 },
                4: { cellWidth: 15, halign: 'center' },
                5: { cellWidth: 20, halign: 'center' },
            }
        });

        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: System      |      Date: ${dateStr}      |      Period: ${periodLabelForPdf}`, 15, pageHeight - 9);

        const fileName = `Location_Survey_Reports_${exportPeriodSlug}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        setExportProgress(p => ({ ...p, percent: 100, message: 'PDF file exported successfully!', done: true }));
        showToast('PDF file exported successfully', 'success');
    };

    const columns: Column<NewLocationSurvey>[] = [
        {
            header: 'Survey ID',
            accessor: 'surveyId',
            width: '10%',
            render: (survey) => (
                <span
                    data-allow-native-context
                    className="cursor-text select-text font-medium text-gray-900 dark:text-gray-100"
                >
                    {survey.surveyId}
                </span>
            ),
        },
        { header: 'Address', accessor: 'locationAddress', width: '20%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.locationAddress}</span> },
        { header: 'Request Date', accessor: 'surveyRequestDate', width: '12%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.surveyRequestDate}</span> },
        { header: 'Survey Date', accessor: 'surveyDate', width: '12%', render: (survey) => <span className="text-gray-600 dark:text-gray-400">{survey.surveyDate}</span> },
        { header: 'Space Type', accessor: 'spaceConsistsOf', width: '10%', render: (survey) => <span className="text-gray-900 dark:text-gray-100">{survey.spaceConsistsOf}</span> },
        {
            header: 'Hazardous',
            width: '12%',
            render: (survey) => {
                const surveyQuestions = (survey.questions || {}) as Record<string, string>;
                const hazardFromQuestionsRow = surveyQuestions.fuelStationAvailable === 'Yes' || surveyQuestions.otherAssessmentHazardous === 'Yes';
                const hazardFromDetailsRow = typeof survey.hazardousArea === 'string' && survey.hazardousArea.trim() !== '';
                const hazardFlag = survey.isHazardous ?? (hazardFromQuestionsRow || hazardFromDetailsRow);

                return (
                    <div className="space-y-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${hazardFlag
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}>
                            {hazardFlag ? 'Yes' : 'No'}
                        </span>
                        {hazardFlag && (survey.hazardousArea?.trim() || '').length > 0 && (
                            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-gray-800/40 dark:text-gray-300">
                                {survey.hazardousArea}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Status',
            width: '10%',
            render: (survey) => {
                const computedStatusLabel = computeNewLocationSurveyStatusLabel(survey);
                const normalizedStatus = computedStatusLabel.toLowerCase();

                let colorClasses = 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
                if (normalizedStatus === 'assigned') {
                    colorClasses = 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300';
                } else if (normalizedStatus === 'completed') {
                    colorClasses = 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300';
                } else if (normalizedStatus === 'submitted to regional') {
                    colorClasses = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300';
                } else if (normalizedStatus === 'forwarded') {
                    colorClasses = 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300';
                } else if (normalizedStatus === 'approved') {
                    colorClasses = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
                } else if (normalizedStatus === 'rejected') {
                    colorClasses = 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
                }

                return (
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${colorClasses}`}>
                        {computedStatusLabel}
                    </span>
                );
            }
        }
    ];

    if (isMIS || isRegional || isHead || isIncharge) {
        columns.push({
            header: 'Assigned PSO',
            accessor: 'assignedToName',
            width: '15%',
            render: (survey) => <span className="text-gray-900 dark:text-gray-100">{survey.assignedToName || '—'}</span>,
        });
    }

    if (isIncharge) {
        columns.push({
            header: 'Read Status',
            width: '12%',
            render: (survey: NewLocationSurvey) => {
                const s = (survey.status || '').toLowerCase();
                const isInitialFlow = s === 'assigned' || s === 'submitted' || s === 'submitted to regional';
                if (isInitialFlow) {
                    return <span className="text-gray-400 dark:text-gray-650">—</span>;
                }
                const isRead = !!survey.inchargeRead;
                return (
                    <select
                        value={isRead ? 'read' : 'unread'}
                        onChange={(e) => {
                            const val = e.target.value;
                            void handleSetReadStatus(survey, val === 'read');
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={updatingReadStatus === survey.id}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold border border-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 transition-all duration-200 ${
                            isRead 
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 focus:ring-emerald-500' 
                            : 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800 focus:ring-rose-500 font-bold'
                        }`}
                    >
                        <option value="unread" className="bg-white text-rose-850 dark:bg-gray-800 dark:text-rose-350">Unread</option>
                        <option value="read" className="bg-white text-emerald-850 dark:bg-gray-800 dark:text-emerald-355">Read</option>
                    </select>
                );
            }
        });
    }

    return (
        <>
            <div className="space-y-6 p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Location Surveys</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            View and manage all your location survey reports
                        </p>
                    </div>
                    {(isMIS && !isHead) && (
                        <Button onClick={() => setShowAddModal(true)} className="flex shrink-0 items-center gap-2">
                            <Icon icon={FiPlus} className="h-4 w-4" />
                            Add Survey
                        </Button>
                    )}
                </div>

                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                        <div className="min-w-0 flex-1">
                            <TextField
                                type="text"
                                className="!h-11"
                                placeholder="Survey ID or address (e.g. 0006, street name)…"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applySearch();
                                    }
                                }}
                                prefix={<Icon icon={FiSearch} />}
                                suffix={
                                    searchInput ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchInput('');
                                                setAppliedSearchTerm('');
                                            }}
                                            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <Icon icon={FiX} className="h-4 w-4" />
                                        </button>
                                    ) : null
                                }
                            />
                        </div>
                        <Button onClick={applySearch} className="!h-11 !py-0 flex shrink-0 items-center gap-2">
                            <Icon icon={FiSearch} className="h-4 w-4" />
                            Search
                        </Button>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => setExportMenuOpen((o) => !o)}
                                className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Export
                                <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {exportMenuOpen && (
                                <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            exportToExcel();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">XLS</span>
                                        <span>
                                            <span className="block">Excel workbook</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">All rows in the current result list</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                        onClick={() => {
                                            setExportMenuOpen(false);
                                            exportToPDF();
                                        }}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">PDF</span>
                                        <span>
                                            <span className="block">PDF report</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">All rows in the current result list</span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button variant="secondary" onClick={openFilterModal} className="flex items-center gap-2">
                            <Icon icon={FiFileText} className="h-4 w-4" />
                            Filters
                        </Button>
                        {hasActiveLocationFilters && (
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        )}
                    </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Search matches <span className="font-medium text-gray-700 dark:text-gray-300">Survey ID</span> and{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">address</span> (any substring, e.g. last segment of an ID).{' '}
                    Click a row to open details; right-click for View / Submit when available.
                    {hitScanLimit && needsClientScan ? ' Some older matches may be excluded until you narrow filters.' : ''}
                </p>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    loadingVariant="overlay"
                    draggable={false}
                    emptyMessage={
                        appliedSearchTerm || hasActiveLocationFilters
                            ? 'Try adjusting your search or filter'
                            : 'Get started by creating your first survey'
                    }
                    onRowClick={(survey) => {
                        if (typeof window !== 'undefined' && window.getSelection()?.toString()) return;
                        openDetailsModal(survey, false);
                    }}
                    onRowContextMenu={(e, survey) => {
                        const t = e.target as HTMLElement;
                        if (t.closest('[data-allow-native-context]')) return;
                        e.preventDefault();
                        setRowContextMenu({ survey, clientX: e.clientX, clientY: e.clientY });
                    }}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: (page) => {
                            if (page === currentPage) return;
                            void loadPage(page);
                        },
                    }}
                />
            </div>

            {rowContextMenu && (() => {
                const survey = rowContextMenu.survey;
                const computedStatusLabel = computeNewLocationSurveyStatusLabel(survey);
                const normalizedStatus = computedStatusLabel.toLowerCase();
                const isAssigned = normalizedStatus === 'assigned';
                const isSubmitted = normalizedStatus === 'submitted';
                const isPsoOnly = !isBankUser && !isMIS && !isRegional && !isHead;
                const showSubmitRemaining = isPsoOnly && isAssigned && survey.assignedTo === uid;
                return (
                    <div
                        id="location-surveys-row-context"
                        role="menu"
                        className="fixed z-[200] min-w-[11rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-900"
                        style={{
                            left: Math.min(
                                rowContextMenu.clientX,
                                (typeof window !== 'undefined' ? window.innerWidth : 1200) - 180
                            ),
                            top: Math.min(
                                rowContextMenu.clientY,
                                (typeof window !== 'undefined' ? window.innerHeight : 800) - 8
                            ),
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                            onClick={() => {
                                openDetailsModal(survey, false);
                                setRowContextMenu(null);
                            }}
                        >
                            <Icon icon={FiEye} className="h-4 w-4 shrink-0 opacity-70" />
                            View
                        </button>
                        {isBankUser && (
                            <>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20"
                                    onClick={() => {
                                        openDetailsModal(survey, true);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiEdit2} className="h-4 w-4 shrink-0 opacity-80" />
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20"
                                    onClick={() => {
                                        handleDeleteSurvey(survey);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiTrash2} className="h-4 w-4 shrink-0 opacity-80" />
                                    Delete
                                </button>
                            </>
                        )}
                        {showSubmitRemaining && (
                            <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-950/20"
                                onClick={() => {
                                    openDetailsModal(survey, true);
                                    setRowContextMenu(null);
                                }}
                            >
                                <Icon icon={FiEdit2} className="h-4 w-4 shrink-0 opacity-80" />
                                Submission
                            </button>
                        )}
                        {isSubmitted && (
                            <button
                                type="button"
                                role="menuitem"
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/20"
                                onClick={() => {
                                    exportSingleSurveyToPDF(survey);
                                    setRowContextMenu(null);
                                }}
                            >
                                <Icon icon={FiFileText} className="h-4 w-4 shrink-0 opacity-80" />
                                Download PDF
                            </button>
                        )}
                    </div>
                );
            })()}

            <Modal isOpen={showFilterModal} onClose={() => setShowFilterModal(false)} title="Filter location surveys" size="lg">
                <div className="space-y-4 p-4">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Request date</label>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Filters by the survey request date shown on each row.</p>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftRequestDateFrom && draftRequestDateTo
                                    ? [dayjs(draftRequestDateFrom), dayjs(draftRequestDateTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftRequestDateFrom(null);
                                    setDraftRequestDateTo(null);
                                    return;
                                }
                                setDraftRequestDateFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftRequestDateTo(dates[1].format('YYYY-MM-DD'));
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Survey date</label>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Filters by the on-site survey date field.</p>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftSurveyDateFrom && draftSurveyDateTo
                                    ? [dayjs(draftSurveyDateFrom), dayjs(draftSurveyDateTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftSurveyDateFrom(null);
                                    setDraftSurveyDateTo(null);
                                    return;
                                }
                                setDraftSurveyDateFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftSurveyDateTo(dates[1].format('YYYY-MM-DD'));
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Assigned PSO</label>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Names are loaded from recent surveys when you open this dialog.</p>
                        <select
                            value={draftAssignedPsoFilter}
                            onChange={(e) => setDraftAssignedPsoFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All PSO</option>
                            {psoNameOptions.map((name) => (
                                <option key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                        <select
                            value={draftStatusFilter}
                            onChange={(e) => setDraftStatusFilter(e.target.value as LocationSurveyStatusFilter)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All statuses</option>
                            <option value="assigned">Assigned</option>
                            <option value="completed">Completed</option>
                            <option value="submitted">Submitted</option>
                            <option value="submitted to regional">Submitted to Regional</option>
                            <option value="forwarded">Forwarded</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="pending">Pending / no status</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Hazardous</label>
                        <select
                            value={draftHazardousFilter}
                            onChange={(e) => setDraftHazardousFilter(e.target.value as LocationSurveyHazardFilter)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All</option>
                            <option value="yes">Hazardous — Yes</option>
                            <option value="no">Hazardous — No</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={applyFilters}>Apply</Button>
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Add Survey Modal */}
            <AddNewLocationSurveyModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleSurveyAdded}
            />

            {/* Details Modal */}
            <NewLocationSurveyDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                survey={selectedSurvey}
                onSuccess={handleSurveyAdded}
                autoEdit={detailsAutoEdit}
                onDownloadPDF={exportSingleSurveyToPDF}
                onToggleReadStatus={handleSetReadStatus}
            />

            {/* Export Progress Overlay */}
            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel' : 'Exporting PDF'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">{Math.round(exportProgress.percent)}%</span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <div className="flex flex-col gap-1 text-xs text-slate-600 dark:text-slate-300">
                            <p className="font-medium text-slate-800 dark:text-slate-200">{exportProgress.message}</p>
                            {exportProgress.totalRows > 0 && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                    Processed: <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.processedRows}</span> of <span className="font-semibold text-slate-700 dark:text-slate-300">{exportProgress.totalRows}</span> surveys
                                </p>
                            )}
                        </div>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            {!exportProgress.done ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        cancelExportRef.current = true;
                                    }}
                                    className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition dark:border-red-900/30 dark:bg-red-950/20 dark:text-red-400 dark:hover:bg-red-900/30"
                                >
                                    Cancel
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setExportProgress(p => ({ ...p, active: false }));
                                    }}
                                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
